import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Clock, DollarSign, CheckCircle, Trophy, 
  ArrowRight, AlertCircle, Calendar, TrendingUp, BarChart3,
  BookOpen, Target, Users, Building, Info, Play, Lock,
  ChevronRight, Award, Star, Zap, Wifi, WifiOff
} from 'lucide-react';

const OrgAdminAssessmentDashboard = () => {
  const [assessmentPhases, setAssessmentPhases] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [assessmentConfig, setAssessmentConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [evaluatorStatus, setEvaluatorStatus] = useState({ is_online: false, evaluator_name: null });
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Get organization ID from multiple sources with debugging
  const getOrganizationId = () => {
    // Try localStorage first
    const localOrgId = localStorage.getItem('organization_id');
    if (localOrgId && localOrgId !== 'null' && localOrgId !== '') {
      console.log('[Dashboard] Found org_id in localStorage:', localOrgId);
      return localOrgId;
    }
    
    // Try sessionStorage
    const sessionOrgId = sessionStorage.getItem('cness_organization_id');
    if (sessionOrgId && sessionOrgId !== 'null' && sessionOrgId !== '') {
      console.log('[Dashboard] Found org_id in sessionStorage:', sessionOrgId);
      return sessionOrgId;
    }
    
    // Try userData in localStorage
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      if (userData.organization_id) {
        console.log('[Dashboard] Found org_id in userData:', userData.organization_id);
        return userData.organization_id;
      }
    } catch (e) {
      console.error('[Dashboard] Error parsing userData:', e);
    }
    
    console.warn('[Dashboard] No organization_id found in any storage');
    return null;
  };
  
  const organizationId = getOrganizationId();

  useEffect(() => {
    fetchData();
    // Fetch evaluator status immediately and then poll every 15 seconds
    fetchEvaluatorStatus();
    const statusInterval = setInterval(fetchEvaluatorStatus, 15000);
    return () => clearInterval(statusInterval);
  }, []);

  const fetchEvaluatorStatus = async () => {
    if (!organizationId) {
      console.log('[OrgDashboard] No organizationId for status check');
      return;
    }
    try {
      console.log('[OrgDashboard] Fetching evaluator status for:', organizationId);
      const response = await fetch(`${BACKEND_URL}/api/evaluator/online-status/${organizationId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('[OrgDashboard] Evaluator status:', data);
        setEvaluatorStatus(data);
      } else {
        console.log('[OrgDashboard] Status fetch failed:', response.status);
      }
    } catch (e) {
      console.log('[OrgDashboard] Error fetching evaluator status:', e);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch organization details
      if (organizationId) {
        const orgResponse = await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}`);
        if (orgResponse.ok) {
          const orgData = await orgResponse.json();
          setOrganization(orgData);
          
          // Fetch assessment config based on company size
          const size = orgData.organization_size || 'MEDIUM';
          const configResponse = await fetch(`${BACKEND_URL}/api/assessment/config/${size}`);
          if (configResponse.ok) {
            const configData = await configResponse.json();
            setAssessmentConfig(configData);
          }
        }
      }
      
      // Fetch assessment phases
      const phasesResponse = await fetch(`${BACKEND_URL}/api/assessment-phases/${organizationId}`);
      if (phasesResponse.ok) {
        const phasesData = await phasesResponse.json();
        setAssessmentPhases(phasesData);
      }
      
      // Fetch evaluator online status
      await fetchEvaluatorStatus();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCompanySizeInfo = () => {
    if (!assessmentConfig?.config) return null;
    const config = assessmentConfig.config;
    return {
      size: config.name,
      displayName: config.display_name,
      totalQuestions: config.total_questions,
      qualitative: config.qualitative_questions,
      quantitative: config.quantitative_questions,
      fee: config.assessment_fee,
      employeeRange: config.employee_range
    };
  };

  const companyInfo = getCompanySizeInfo();

  const startAssessment = async () => {
    // Check if assessment phase exists, if not create one
    if (assessmentPhases.length === 0) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/assessment/generate-questions/${organizationId}`, {
          method: 'POST'
        });
        if (response.ok) {
          const data = await response.json();
          // Refresh data
          fetchData();
          // Navigate to assessment
          navigate(`/org-assessment/enhanced/${data.session_id}`, { 
            state: { organizationId } 
          });
        }
      } catch (error) {
        console.error('Error starting assessment:', error);
      }
    } else {
      // Use existing assessment
      const phase = assessmentPhases[0];
      navigate(`/org-assessment/enhanced/${phase.assessment_id}`, { 
        state: { organizationId } 
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your assessment dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black mb-2">Assessment Center</h1>
              <p className="text-indigo-100">
                {organization?.name || 'Your Organization'} • {companyInfo?.displayName || 'Loading...'}
              </p>
            </div>
            {organization?.certification_tier && (
              <div className="text-right">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-3">
                  <p className="text-sm text-indigo-100">Current Certification</p>
                  <p className="text-2xl font-black">{organization.certification_tier}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {[
              { id: 'info', label: 'Information Board', icon: Info },
              { id: 'dashboard', label: 'Assessment Dashboard', icon: Target },
              { id: 'analytics', label: 'Analytics & Reports', icon: BarChart3, disabled: !organization?.certification_tier }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`flex items-center gap-2 px-6 py-4 font-semibold border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : tab.disabled
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : 'border-transparent text-gray-600 hover:text-indigo-600 hover:border-indigo-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
                {tab.disabled && <Lock className="w-4 h-4" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* INFORMATION BOARD TAB */}
        {activeTab === 'info' && (
          <div className="space-y-8">
            {/* Company Size & Questions Info */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Company Info Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-indigo-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Building className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Your Company Size</h3>
                    <p className="text-indigo-600 font-black text-xl">{companyInfo?.size || 'N/A'}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">{companyInfo?.employeeRange || ''}</p>
              </div>

              {/* Questions Info Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-purple-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Total Questions</h3>
                    <p className="text-purple-600 font-black text-xl">{companyInfo?.totalQuestions || 0}</p>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Qualitative: <strong>{companyInfo?.qualitative || 0}</strong></span>
                  <span className="text-gray-600">Quantitative: <strong>{companyInfo?.quantitative || 0}</strong></span>
                </div>
              </div>

              {/* Fee Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-green-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Assessment Fee</h3>
                    <p className="text-green-600 font-black text-xl">${companyInfo?.fee?.toLocaleString() || 0}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">One-time certification fee</p>
              </div>
            </div>

            {/* Assessment Roadmap */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                <Target className="w-7 h-7 text-indigo-600" />
                Your Assessment Roadmap
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {/* Step 1: Qualitative */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">1</div>
                    <h4 className="font-bold text-gray-900 mb-2">Qualitative</h4>
                    <p className="text-sm text-gray-600 mb-2">Self-Assessment</p>
                    <p className="text-blue-600 font-bold">{companyInfo?.qualitative || 0} Questions</p>
                  </div>
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-6 text-gray-300" />
                </div>

                {/* Step 2: Payment */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">2</div>
                    <h4 className="font-bold text-gray-900 mb-2">Payment</h4>
                    <p className="text-sm text-gray-600 mb-2">Certification Fee</p>
                    <p className="text-green-600 font-bold">${companyInfo?.fee?.toLocaleString() || 0}</p>
                  </div>
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-6 text-gray-300" />
                </div>

                {/* Step 3: Quantitative */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">3</div>
                    <h4 className="font-bold text-gray-900 mb-2">Quantitative</h4>
                    <p className="text-sm text-gray-600 mb-2">Data Assessment</p>
                    <p className="text-purple-600 font-bold">{companyInfo?.quantitative || 0} Questions</p>
                  </div>
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-6 text-gray-300" />
                </div>

                {/* Step 4: Evaluation */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">4</div>
                    <h4 className="font-bold text-gray-900 mb-2">Evaluation</h4>
                    <p className="text-sm text-gray-600 mb-2">Expert Review</p>
                    <p className="text-orange-600 font-bold">~2 Weeks</p>
                  </div>
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-6 text-gray-300" />
                </div>

                {/* Step 5: Results */}
                <div>
                  <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-400 rounded-xl p-5 text-center">
                    <div className="w-10 h-10 bg-yellow-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                      <Trophy className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">Results</h4>
                    <p className="text-sm text-gray-600 mb-2">Certification</p>
                    <p className="text-yellow-600 font-bold">Your Score</p>
                  </div>
                </div>
              </div>

              {/* Timeline Info */}
              <div className="mt-8 bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                <div className="flex items-start gap-4">
                  <Clock className="w-8 h-8 text-indigo-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-indigo-900 mb-2">Assessment Timeline</h4>
                    <p className="text-indigo-800 text-sm">
                      After completing both Qualitative and Quantitative phases, our expert evaluators will review your responses. 
                      <strong> This evaluation process takes approximately 2 weeks.</strong> You will receive detailed feedback 
                      and your certification results via email and on this dashboard.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Certification Tiers Info */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3">
                <Award className="w-7 h-7 text-yellow-500" />
                Certification Tiers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-yellow-400 rounded-xl p-6 text-center">
                  <Star className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                  <h4 className="font-black text-xl text-gray-900 mb-1">Luminary</h4>
                  <p className="text-yellow-600 font-bold mb-2">85-100 Points</p>
                  <p className="text-sm text-gray-600">Industry leader setting the standard</p>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-gray-300 rounded-xl p-6 text-center">
                  <Zap className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                  <h4 className="font-black text-xl text-gray-900 mb-1">Inspired</h4>
                  <p className="text-gray-600 font-bold mb-2">65-84 Points</p>
                  <p className="text-sm text-gray-600">Strong performance with growth potential</p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-6 text-center">
                  <Target className="w-10 h-10 text-orange-500 mx-auto mb-3" />
                  <h4 className="font-black text-xl text-gray-900 mb-1">Aspiring</h4>
                  <p className="text-orange-600 font-bold mb-2">40-64 Points</p>
                  <p className="text-sm text-gray-600">Emerging organization with clear direction</p>
                </div>
              </div>
            </div>

            {/* Start Assessment CTA */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white text-center">
              <h2 className="text-2xl font-black mb-4">Ready to Begin Your Assessment?</h2>
              <p className="text-indigo-100 mb-6 max-w-2xl mx-auto">
                Complete your assessment to receive your CNESS certification. 
                You can learn about the 6 Pillars before starting or begin directly.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => navigate('/platform')}
                  className="px-8 py-3 bg-white/20 hover:bg-white/30 rounded-xl font-bold transition-all flex items-center gap-2"
                >
                  <BookOpen className="w-5 h-5" />
                  Learn 6 Pillars First
                </button>
                <button
                  onClick={startAssessment}
                  className="px-8 py-3 bg-white text-indigo-600 rounded-xl font-bold hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Start Assessment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ASSESSMENT DASHBOARD TAB */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {assessmentPhases.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Assessment Started</h3>
                <p className="text-gray-500 mb-6">Start your assessment from the Information Board to begin your certification journey.</p>
                <button
                  onClick={() => setActiveTab('info')}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all"
                >
                  Go to Information Board
                </button>
              </div>
            ) : (
              assessmentPhases.map((phase) => (
                <div key={phase.assessment_id} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  {/* Assessment Header */}
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-black mb-1">{phase.assessment_name || 'CNESS Assessment'}</h2>
                        <p className="text-indigo-100">{phase.assessment_code || phase.assessment_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-indigo-100">Status</p>
                        <p className="text-xl font-bold capitalize">{phase.overall_status?.replace('_', ' ') || 'In Progress'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Phase Progress */}
                  <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      {/* Phase 1: Qualitative */}
                      <PhaseCard
                        number={1}
                        title="Qualitative"
                        subtitle={`${companyInfo?.qualitative || 0} Questions`}
                        status={phase.qualitative_status}
                        progress={phase.qualitative_progress}
                        completedCount={phase.qualitative_responses_count}
                        totalCount={companyInfo?.qualitative}
                        icon={FileText}
                        color="blue"
                        onClick={() => navigate(`/org-assessment/enhanced/${phase.assessment_id}`, { state: { organizationId } })}
                        locked={false}
                      />

                      {/* Phase 2: Payment */}
                      <PhaseCard
                        number={2}
                        title="Payment"
                        subtitle={`$${companyInfo?.fee?.toLocaleString() || 0}`}
                        status={phase.payment_status}
                        icon={DollarSign}
                        color="green"
                        onClick={() => navigate(`/org-assessment/payment/${phase.assessment_id}`, { state: { organizationId } })}
                        locked={phase.qualitative_status !== 'completed'}
                      />

                      {/* Phase 3: Quantitative */}
                      <PhaseCard
                        number={3}
                        title="Quantitative"
                        subtitle={`${companyInfo?.quantitative || 0} Questions`}
                        status={phase.quantitative_status}
                        progress={phase.quantitative_progress}
                        icon={BarChart3}
                        color="purple"
                        onClick={() => navigate(`/org-assessment/quantitative/${phase.assessment_id}`, { state: { organizationId } })}
                        locked={phase.payment_status !== 'completed'}
                      />

                      {/* Phase 4: Evaluation */}
                      <PhaseCard
                        number={4}
                        title="Evaluation"
                        subtitle="Expert Review"
                        status={phase.evaluation_status}
                        icon={CheckCircle}
                        color="orange"
                        locked={phase.quantitative_status !== 'completed'}
                        evaluatorOnline={evaluatorStatus.is_online}
                        evaluatorName={evaluatorStatus.evaluator_name}
                      />

                      {/* Phase 5: Results */}
                      <PhaseCard
                        number={5}
                        title="Results"
                        subtitle="Certification"
                        status={phase.results_published ? 'completed' : 'pending'}
                        icon={Trophy}
                        color="yellow"
                        onClick={() => navigate(`/org-assessment/results/${phase.assessment_id}`, { state: { organizationId } })}
                        locked={phase.evaluation_status !== 'completed'}
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-gray-50 px-8 py-4 border-t flex justify-between items-center">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      Started: {phase.created_at ? new Date(phase.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Company Size: </span>
                      <span className="font-bold">{phase.company_size || companyInfo?.size}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Analytics Available After Assessment</h3>
              <p className="text-gray-500">Complete your assessment to view detailed analytics and pillar scores.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Phase Card Component
const PhaseCard = ({ number, title, subtitle, status, progress, completedCount, totalCount, icon: Icon, color, onClick, locked, evaluatorOnline, evaluatorName }) => {
  const isCompleted = status === 'completed';
  const isInProgress = status === 'in_progress';
  const isActive = isInProgress || (status === 'not_started' && !locked);

  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-500', gradient: 'from-blue-50 to-indigo-50' },
    green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-500', gradient: 'from-green-50 to-emerald-50' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-500', gradient: 'from-purple-50 to-violet-50' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-500', gradient: 'from-orange-50 to-amber-50' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', border: 'border-yellow-500', gradient: 'from-yellow-50 to-amber-50' }
  };

  const c = colorClasses[color] || colorClasses.blue;

  return (
    <div
      className={`relative bg-gradient-to-br ${c.gradient} border-2 rounded-xl p-5 transition-all ${
        isCompleted ? 'border-green-500' :
        isActive ? `${c.border} shadow-lg` :
        locked ? 'border-gray-200 opacity-50' :
        'border-gray-200'
      } ${onClick && !locked ? 'cursor-pointer hover:shadow-md' : ''}`}
      onClick={() => onClick && !locked && onClick()}
    >
      {/* Number Badge */}
      <div className={`absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        isCompleted ? 'bg-green-500 text-white' :
        isActive ? `${c.bg} ${c.text}` :
        'bg-gray-200 text-gray-600'
      }`}>
        {isCompleted ? <CheckCircle className="w-5 h-5" /> : number}
      </div>

      {/* Lock Icon */}
      {locked && (
        <div className="absolute -top-2 -right-2">
          <Lock className="w-5 h-5 text-gray-400" />
        </div>
      )}

      {/* Icon */}
      <div className={`inline-flex p-3 rounded-lg mb-3 ${isCompleted ? 'bg-green-100' : c.bg}`}>
        <Icon className={`w-6 h-6 ${isCompleted ? 'text-green-600' : c.text}`} />
      </div>

      {/* Title */}
      <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
      <p className={`text-sm font-semibold ${isCompleted ? 'text-green-600' : c.text}`}>{subtitle}</p>

      {/* Progress */}
      {progress !== undefined && isInProgress && totalCount && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Progress</span>
            <span>{completedCount || 0}/{totalCount}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${c.bg.replace('100', '500')}`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Evaluator Online Status (for Evaluation phase) */}
      {title === 'Evaluation' && !locked && (
        <div className="mt-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${evaluatorOnline ? 'bg-green-100' : 'bg-gray-100'}`}>
            {evaluatorOnline ? (
              <>
                <Wifi className="w-4 h-4 text-green-600" />
                <span className="text-xs font-semibold text-green-700">
                  Online {evaluatorName && `- ${evaluatorName}`}
                </span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-semibold text-gray-500">Offline</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Status */}
      <div className="mt-3">
        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
          isCompleted ? 'bg-green-100 text-green-700' :
          isInProgress ? 'bg-blue-100 text-blue-700' :
          locked ? 'bg-gray-100 text-gray-500' :
          'bg-gray-100 text-gray-600'
        }`}>
          {isCompleted ? 'Completed' : isInProgress ? 'In Progress' : locked ? 'Locked' : 'Not Started'}
        </span>
      </div>
    </div>
  );
};

export default OrgAdminAssessmentDashboard;
